/**
 * De-duplicates session-details calls: the purchase tracker and the confirmation page both need the
 * same session. Only COMPLETE sessions are cached — an OPEN one can still transition, and a retry
 * after a failure must reach the API again.
 */
import type { AuthSource } from '@/lib/getUserAuthHeaders'
import { getSessionStatus } from '@/lib/payment-service'

import type { GetCheckoutSessionDetailsResponse } from '@/clients/payment-service'

import type { CheckoutCountry } from './types'

const cache = new Map<string, Promise<GetCheckoutSessionDetailsResponse>>()

const cacheKey = (countryCode: string, sessionId: string, guestEmail?: string) =>
  `${countryCode}:${sessionId}:${guestEmail ?? ''}`

export const fetchSessionDetails = (
  auth: AuthSource,
  countryCode: CheckoutCountry,
  sessionId: string,
  guestEmail?: string,
): Promise<GetCheckoutSessionDetailsResponse> => {
  const key = cacheKey(countryCode, sessionId, guestEmail)
  const cached = cache.get(key)
  if (cached) return cached

  const pending = getSessionStatus(auth, countryCode, sessionId, guestEmail)
  cache.set(key, pending)
  void pending
    .then((session) => {
      if (session.status !== 'COMPLETE') cache.delete(key)
    })
    .catch(() => cache.delete(key))
  return pending
}

/**
 * As {@link fetchSessionDetails}, retrying a failed call with a widening delay. For callers that
 * gate what a customer sees on the answer, where one transient failure should not decide it.
 */
export const fetchSessionDetailsWithRetry = async (
  auth: AuthSource,
  countryCode: CheckoutCountry,
  sessionId: string,
  { retries = 2, delayMs = 500 }: { retries?: number; delayMs?: number } = {},
): Promise<GetCheckoutSessionDetailsResponse> => {
  try {
    return await fetchSessionDetails(auth, countryCode, sessionId)
  } catch (error) {
    if (retries <= 0) throw error
    await new Promise((resolve) => setTimeout(resolve, delayMs))
    return fetchSessionDetailsWithRetry(auth, countryCode, sessionId, { retries: retries - 1, delayMs: delayMs * 2 })
  }
}
